/**
 * Amanah — Succession planning docs for the Life Planning Kit
 *
 * Two new documents added Feb 2026 alongside the existing 8:
 *   08-succession-planning-cover.docx     — an executive letter that walks
 *                                           the family through what
 *                                           succession planning IS and how
 *                                           it complements the Wasiyyah.
 *   09-succession-planning-workbook.docx  — a 12-section fill-in-by-hand
 *                                           workbook covering Cover, Faraid
 *                                           schedule, Wasiyya, executor,
 *                                           guardian, funeral wishes,
 *                                           estate inventory, digital
 *                                           accounts, key contacts,
 *                                           notification list, first-30-days
 *                                           checklist for executor,
 *                                           signature block.
 *
 * Both docs use the shared Amanah brand theme (`amanahDocxTheme.js`) so
 * headers, footers, cover styling, colours and typography are identical to
 * every other doc in the kit.
 */

import {
  buildAmanahSection,
  buildAmanahCover,
  themedHeading,
  themedBody,
  themedBullet,
  AMANAH_COLORS,
  AMANAH_FONTS,
} from './amanahDocxTheme';

const stamp = () => new Date().toISOString().slice(0, 10);

const loadDocx = () => import('docx');

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// -----------------------------------------------------------------
// 08 — Succession Planning cover / executive letter
// -----------------------------------------------------------------
export const buildSuccessionCoverDocx = async ({ asBlob = false } = {}) => {
  const docx = await loadDocx();
  const { Document, Packer, Paragraph, TextRun, AlignmentType } = docx;

  const H = (t, l = 2) => themedHeading(docx, t, l);
  const P = (t, opts) => themedBody(docx, t, opts);
  const Li = (t) => themedBullet(docx, t);

  const doc = new Document({
    sections: [buildAmanahSection(docx, {
      docTitle: 'Succession Planning · Executive Letter',
      contextTag: 'Succession · Cover',
      children: [
        ...buildAmanahCover(docx, {
          kicker: 'AMANAH LIFE PLANNING KIT · 08',
          title: 'Succession Planning',
          subtitle: 'Executive letter for your family & executor',
        }),

        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          children: [new TextRun({ text: 'بسم الله الرحمن الرحيم', size: 28, color: AMANAH_COLORS.emerald, font: AMANAH_FONTS.serif })],
        }),

        P('Assalamu alaykum wa rahmatullahi wa barakatuh.'),

        P('This document — together with the Succession Workbook (09) — is what your executor and family should hold on the day something happens to you. The Wasiyyah (05) tells them what your religious wishes are. The Faraid worksheet (04) tells them who inherits. This Succession pack tells them exactly what to do, in what order, on days 1 through 30.'),

        H('What "succession planning" actually means'),

        P('It is the operational counterpart to the Wasiyyah. Where the Wasiyyah answers questions about faith and shares, the succession pack answers practical questions:'),
        Li('Who is my executor, and how does anyone reach them?'),
        Li('Which bank accounts, insurance policies and lockers exist, and where are the keys?'),
        Li('Who should be informed within the first 24 hours?'),
        Li('What are my funeral wishes — including which masjid, which cemetery, and any specific du\'a?'),
        Li('What debts must be settled before any distribution?'),
        Li('Where are the physical documents (passport, deeds, marriage certificate)?'),

        H('How this pack complements the rest of the kit'),

        P('The Wasiyyah (05) and this Succession pack are two sides of the same coin. Together they give your family both spiritual clarity ("what does my faith say?") and operational clarity ("what do I do at 3 AM on a Tuesday when I get the call?").'),

        H('Recommended order to fill this pack'),

        Li('Start with the End-of-Life Checklist (06) — it surfaces your funeral wishes and immediate-notification list.'),
        Li('Complete the Inheritance Worksheet (04) so the Faraid schedule is documented.'),
        Li('Sign the Wasiyyah (05) before two adult Muslim witnesses.'),
        Li('Fill Section 1 (Cover) and Section 3 (Executor) of the Workbook (09).'),
        Li('Fill Sections 5-9 of the Workbook — estate inventory, digital accounts, key contacts, notification list.'),
        Li('Print, sign, and store all pages together in a labelled envelope your executor knows to open.'),

        H('Also do this online'),

        P('The interactive version at zakat.international/succession will generate a formatted multi-page PDF that pulls values from your Wasiyyah and Inheritance sessions automatically. Both versions are equally valid — use whichever your family will actually find and use.'),

        H('A prayer'),

        P('May Allah accept your foresight, ease the burden on your loved ones, and make your affairs a source of blessing for those who come after you.', { run: { italics: true } }),

        P('— Amanah by Zakat.International', { run: { bold: true } }),
      ],
    })],
  });

  const blob = await Packer.toBlob(doc);
  if (asBlob) return blob;
  downloadBlob(blob, `08-succession-planning-cover-${stamp()}.docx`);
  return blob;
};

// -----------------------------------------------------------------
// 09 — Succession Planning workbook — 12-section fill-by-hand
// -----------------------------------------------------------------
export const buildSuccessionWorkbookDocx = async ({ asBlob = false } = {}) => {
  const docx = await loadDocx();
  const { Document, Packer, Paragraph, TextRun, AlignmentType, UnderlineType,
          Table, TableRow, TableCell, BorderStyle, WidthType } = docx;

  const H = (t, l = 2) => themedHeading(docx, t, l);
  const P = (t, opts) => themedBody(docx, t, opts);

  const blank = (w = 40) => new TextRun({
    text: '\u00A0'.repeat(w),
    underline: { type: UnderlineType.SINGLE, color: AMANAH_COLORS.stoneSoft }, size: 22,
  });
  const f = (label) => new Paragraph({
    spacing: { after: 160, line: 320 },
    children: [
      new TextRun({ text: `${label}: `, bold: true, size: 22, color: AMANAH_COLORS.emerald, font: AMANAH_FONTS.sans }),
      blank(60),
    ],
  });

  // Utility to render a 3-column mini-table with blank rows.
  const cell = (t, { bold = false, w = 33 } = {}) => new TableCell({
    width: { size: w, type: WidthType.PERCENTAGE },
    children: [new Paragraph({ children: [new TextRun({ text: t, bold, size: 20, color: bold ? AMANAH_COLORS.emerald : AMANAH_COLORS.ink, font: AMANAH_FONTS.sans })] })],
  });
  const blankRow = (n = 3) => new TableRow({ children: Array.from({ length: n }, () => cell('   ')) });
  const table = (headers, blankCount = 6) => new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4, color: AMANAH_COLORS.gold },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: AMANAH_COLORS.gold },
      left: { style: BorderStyle.SINGLE, size: 2, color: AMANAH_COLORS.goldSoft },
      right: { style: BorderStyle.SINGLE, size: 2, color: AMANAH_COLORS.goldSoft },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: AMANAH_COLORS.goldSoft },
      insideVertical: { style: BorderStyle.SINGLE, size: 2, color: AMANAH_COLORS.goldSoft },
    },
    rows: [
      new TableRow({ children: headers.map((h, i) => cell(h, { bold: true, w: [22, 44, 34][i] || 33 })) }),
      ...Array.from({ length: blankCount }, () => blankRow(headers.length)),
    ],
  });

  const doc = new Document({
    sections: [buildAmanahSection(docx, {
      docTitle: 'Succession Planning · Workbook',
      contextTag: 'Succession · Workbook',
      children: [
        ...buildAmanahCover(docx, {
          kicker: 'AMANAH LIFE PLANNING KIT · 09',
          title: 'Succession Workbook',
          subtitle: 'Twelve sections your family will thank you for',
        }),

        new Paragraph({
          alignment: AlignmentType.CENTER, spacing: { after: 200 },
          children: [new TextRun({
            text: '"Whoever has something to bequeath should not let two nights pass without writing it down."',
            italics: true, size: 20, color: AMANAH_COLORS.stone, font: AMANAH_FONTS.serif,
          })],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER, spacing: { after: 320 },
          children: [new TextRun({
            text: '— Sahih al-Bukhari 2738',
            size: 16, color: AMANAH_COLORS.stoneSoft, font: AMANAH_FONTS.sans,
          })],
        }),

        H('1 · Your cover'),
        f('Full legal name'),
        f('National ID / passport no.'),
        f('Date this workbook was filled'),
        f('Country / jurisdiction of residence'),

        H('2 · Statement of intent'),
        P('This workbook is a private operational appendix to my Islamic Will (Wasiyyah). It does not override or replace the Wasiyyah — it exists to give my executor and family the practical information needed to carry out its instructions.'),
        f('Testator signature'),
        f('Date'),

        H('3 · Executor (Wasi)'),
        f('Executor full name'),
        f('Relationship'),
        f('Phone (primary)'),
        f('Email'),
        f('Backup executor name & phone'),

        H('4 · Guardian (for minor children)'),
        f('Guardian full name'),
        f('Relationship & madhhab'),
        f('Contact'),
        f('Backup guardian & contact'),

        H('5 · Funeral wishes'),
        P('Include preferred masjid, cemetery, any specific du\'a, whether you wish to be washed by a specific person, and whether you wish for the janazah to be delayed for travelling relatives.'),
        new Paragraph({ spacing: { after: 800 }, children: [blank(80), blank(80), blank(80), blank(80), blank(80)] }),

        H('6 · Estate inventory — assets'),
        table(['Category', 'Description / location', 'Approx. value'], 8),

        H('7 · Estate inventory — liabilities'),
        table(['Creditor', 'Purpose', 'Balance'], 5),

        H('8 · Digital accounts & credentials'),
        P('Do NOT write actual passwords here. Point your executor to where the passwords live (e.g., a password manager, sealed envelope in a safe deposit box).'),
        table(['Service', 'Username / email', 'Where the password lives'], 6),

        H('9 · Key contacts'),
        table(['Name', 'Role (Imam / Lawyer / Doctor / Employer)', 'Phone'], 6),

        H('10 · Notification list — first 24 hours'),
        P('People who must be informed within the first day. Include out-of-country relatives who may need time to travel.'),
        table(['Name', 'Relationship', 'Phone'], 6),

        H('11 · First 30 days — checklist for your executor'),
        P('Print this section and attach it to your executor\'s copy. Each item is Islamically obligatory or highly recommended.'),
        table(['Day', 'Action', 'Done?'], 10),

        H('12 · Signatures'),
        f('Testator (full name & signature)'),
        f('Witness 1 (full name & signature)'),
        f('Witness 2 (full name & signature)'),
        f('Date'),

        new Paragraph({
          spacing: { before: 320 },
          alignment: AlignmentType.CENTER,
          children: [new TextRun({
            text: '— End of workbook —',
            italics: true, size: 16, color: AMANAH_COLORS.stoneSoft, font: AMANAH_FONTS.sans,
          })],
        }),
      ],
    })],
  });

  const blob = await Packer.toBlob(doc);
  if (asBlob) return blob;
  downloadBlob(blob, `09-succession-planning-workbook-${stamp()}.docx`);
  return blob;
};
